<template>
  <div class="notification-bell" @click="toggleModal">
    <div class="bell-icon" :class="{ 'has-notifications': hasActiveProcessing }">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
      </svg>
      <div v-if="activeRequestsCount > 0" class="notification-badge">
        {{ activeRequestsCount }}
      </div>
    </div>
    
    <!-- Notification Modal -->
    <div v-if="showModal" class="notification-modal" @click.stop>
      <div class="modal-header">
        <h3>Notifications</h3>
        <button class="close-btn" @click="closeModal">&times;</button>
      </div>
      
      <div class="modal-content">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading notifications...</p>
        </div>
        
        <!-- Upload Notifications Section -->
        <div v-if="uploadNotifications.length > 0" class="notifications-section">
          <div class="section-header">
            <h4>File Uploads</h4>
          </div>
          <div 
            v-for="notification in uploadNotifications" 
            :key="notification.id"
            class="upload-notification-item"
            :class="{ 'unread': !notification.read }"
          >
            <div class="notification-header">
              <div class="notification-title">{{ notification.project_name }} - Draw {{ notification.draw_number }}</div>
              <div v-if="!notification.read" class="unread-badge">NEW</div>
            </div>
            
            <div class="notification-details">
              <div class="notification-info">
                <span class="file-count">{{ notification.file_count }} file(s) uploaded</span>
                <span class="uploader">by {{ notification.uploader_name }}</span>
              </div>
              <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
            </div>
            
            <div class="notification-actions">
              <button 
                class="download-btn" 
                @click="viewNotificationDetail(notification)"
              >
                View Details
              </button>
              <button 
                v-if="!notification.read"
                class="mark-read-btn" 
                @click.stop="markNotificationRead(notification.id)"
              >
                Mark Read
              </button>
            </div>
          </div>
        </div>
        
        <!-- PDF Processing Queue Section -->
        <div v-if="userRequests.length > 0" class="notifications-section">
          <div class="section-header">
            <h4>PDF Processing Queue</h4>
          </div>
          <div class="requests-list">
          <div 
            v-for="request in userRequests" 
            :key="request.id"
            class="request-item"
            :class="{ 
              'processing': request.status === 'processing',
              'queued': request.status === 'queued',
              'completed': request.status === 'completed',
              'failed': request.status === 'failed'
            }"
          >
            <div class="request-header">
              <div class="request-title">{{ request.display_name }}</div>
              <div class="request-status" :class="request.status">
                {{ getStatusText(request.status) }}
              </div>
            </div>
            
            <div class="request-details">
              <div class="progress-info">
                <div class="progress-text">{{ request.current_step }}</div>
                <div v-if="request.status === 'processing'" class="progress-bar">
                  <div 
                    class="progress-fill" 
                    :style="{ width: request.progress_percentage + '%' }"
                  ></div>
                </div>
                <div v-if="request.status === 'queued'" class="queue-info">
                  Position in queue: #{{ request.queue_position }}
                </div>
              </div>
              
              <div class="request-meta">
                <span class="pages-info">{{ request.processed_pages }}/{{ request.total_pages }} pages</span>
                <span class="time-info">{{ formatTime(request.created_at) }}</span>
              </div>
              
              <div v-if="request.can_cancel" class="request-actions">
                <button 
                  class="cancel-btn" 
                  @click="cancelRequest(request.id)"
                  :disabled="cancelling === request.id"
                >
                  {{ cancelling === request.id ? 'Cancelling...' : 'Cancel' }}
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
        
        <!-- Empty state if no notifications at all -->
        <div v-if="userRequests.length === 0 && uploadNotifications.length === 0 && !loading" class="empty-state">
          <p>No notifications</p>
          <small>Your notifications will appear here</small>
        </div>
        
        <div v-if="queueStats && userRequests.length > 0" class="queue-stats">
          <div class="stats-row">
            <div class="stat-item">
              <span class="stat-value">{{ queueStats.total_queued }}</span>
              <span class="stat-label">Queued</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ queueStats.currently_processing }}</span>
              <span class="stat-label">Processing</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ queueStats.completed_today }}</span>
              <span class="stat-label">Completed Today</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal backdrop -->
    <div v-if="showModal" class="modal-backdrop" @click="closeModal"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()

// State
const showModal = ref(false)
const loading = ref(false)
const userRequests = ref([])
const queueStats = ref(null)
const cancelling = ref(null)
const pollInterval = ref(null)
const uploadNotifications = ref([])
const unreadUploadCount = ref(0)

// Computed
const hasActiveProcessing = computed(() => {
  return userRequests.value.some(r => r.status === 'processing' || r.status === 'queued')
})

const activeRequestsCount = computed(() => {
  const pdfCount = userRequests.value.filter(r => r.status === 'processing' || r.status === 'queued').length
  return pdfCount + unreadUploadCount.value
})

// Methods
const toggleModal = () => {
  if (showModal.value) {
    closeModal()
  } else {
    openModal()
  }
}

const openModal = async () => {
  showModal.value = true
  await Promise.all([fetchQueueStatus(), fetchUploadNotifications()])
  startPolling()
}

const closeModal = () => {
  showModal.value = false
  stopPolling()
}

const fetchQueueStatus = async () => {
  try {
    loading.value = true
    const response = await axios.get('/api/v1/pdf_multi_doc/queue_status')
    userRequests.value = response.data.user_requests || []
    queueStats.value = response.data.queue_stats || null
  } catch (error) {
    console.error('Error fetching queue status:', error)
  } finally {
    loading.value = false
  }
}

const fetchUploadNotifications = async () => {
  try {
    const response = await axios.get('/api/v1/notifications', {
      params: { notification_type: 'upload_ready', limit: 20 }
    })
    uploadNotifications.value = response.data.notifications || []
    unreadUploadCount.value = response.data.unread_count || 0
  } catch (error) {
    console.error('Error fetching upload notifications:', error)
  }
}

const markNotificationRead = async (notificationId) => {
  try {
    await axios.patch(`/api/v1/notifications/${notificationId}/mark_read`)
    await fetchUploadNotifications()
  } catch (error) {
    console.error('Error marking notification as read:', error)
  }
}

const viewNotificationDetail = (notification) => {
  // Navigate to the notification detail page
  router.push(`/notifications/${notification.id}`)
  closeModal()
}

const cancelRequest = async (requestId) => {
  try {
    cancelling.value = requestId
    await axios.post(`/api/v1/pdf_multi_doc/${requestId}/cancel`)
    await fetchQueueStatus() // Refresh the list
  } catch (error) {
    console.error('Error cancelling request:', error)
  } finally {
    cancelling.value = null
  }
}

const getStatusText = (status) => {
  const statusMap = {
    'queued': 'Queued',
    'processing': 'Processing',
    'completed': 'Completed',
    'failed': 'Failed',
    'cancelled': 'Cancelled'
  }
  return statusMap[status] || status
}

const formatTime = (timestamp) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

const startPolling = () => {
  if (pollInterval.value) return
  
  pollInterval.value = setInterval(() => {
    if (showModal.value && hasActiveProcessing.value) {
      fetchQueueStatus()
    }
  }, 3000) // Poll every 3 seconds when there are active requests
}

const stopPolling = () => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value)
    pollInterval.value = null
  }
}

// Initial fetch for notification badge
onMounted(() => {
  fetchQueueStatus()
  fetchUploadNotifications()
  
  // Set up periodic check for notification badge (less frequent)
  const badgeInterval = setInterval(() => {
    if (!showModal.value) {
      fetchQueueStatus()
      fetchUploadNotifications()
    }
  }, 10000) // Check every 10 seconds for badge updates
  
  onUnmounted(() => {
    clearInterval(badgeInterval)
    stopPolling()
  })
})
</script>

<style scoped lang="scss">
.notification-bell {
  position: relative;
  margin-right: 10px;
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
  line-height: 1;
}

.bell-icon {
  position: relative;
  color: white;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &.has-notifications {
    color: #fbbf24; // Yellow color for active notifications
    
    svg {
      animation: gentle-ring 2s ease-in-out infinite;
    }
  }
}

.notification-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background-color: #ef4444;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  border: 2px solid #152742;
}

.notification-modal {
  position: absolute;
  top: 40px;
  right: 0;
  width: 400px;
  max-height: 500px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  z-index: 1000;
  overflow: hidden;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: transparent;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      color: #374151;
    }
  }
}

.modal-content {
  max-height: 400px;
  overflow-y: auto;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
  
  p {
    color: #6b7280;
    margin: 0;
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  
  p {
    color: #374151;
    margin: 0 0 8px 0;
    font-weight: 500;
  }
  
  small {
    color: #6b7280;
  }
}

.notifications-section {
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
}

.section-header {
  padding: 12px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  
  h4 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.upload-notification-item {
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &.unread {
    background: #eff6ff;
    border-left: 4px solid #3b82f6;
  }
  
  &:hover {
    background: #f9fafb;
  }
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.notification-title {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
  flex: 1;
  margin-right: 12px;
}

.unread-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  background: #3b82f6;
  color: white;
}

.notification-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.notification-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  
  .file-count {
    font-weight: 500;
    color: #374151;
  }
  
  .uploader {
    font-size: 11px;
  }
}

.notification-time {
  font-size: 11px;
  color: #9ca3af;
}

.notification-actions {
  display: flex;
  gap: 8px;
}

.download-btn, .mark-read-btn {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.download-btn {
  background: #3b82f6;
  color: white;
  
  &:hover {
    background: #2563eb;
  }
}

.mark-read-btn {
  background: #e5e7eb;
  color: #374151;
  
  &:hover {
    background: #d1d5db;
  }
}

.requests-list {
  padding: 0;
}

.request-item {
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
  
  &.processing {
    background: #f0f9ff;
    border-left: 4px solid #3b82f6;
  }
  
  &.queued {
    background: #fffbeb;
    border-left: 4px solid #f59e0b;
  }
  
  &.completed {
    background: #f0fdf4;
    border-left: 4px solid #10b981;
  }
  
  &.failed {
    background: #fef2f2;
    border-left: 4px solid #ef4444;
  }
}

.request-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.request-title {
  font-weight: 500;
  color: #111827;
  font-size: 14px;
  flex: 1;
  margin-right: 12px;
}

.request-status {
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  
  &.queued {
    background: #fef3c7;
    color: #92400e;
  }
  
  &.processing {
    background: #dbeafe;
    color: #1e40af;
  }
  
  &.completed {
    background: #d1fae5;
    color: #065f46;
  }
  
  &.failed {
    background: #fee2e2;
    color: #991b1b;
  }
}

.progress-info {
  margin-bottom: 8px;
}

.progress-text {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  
  .progress-fill {
    height: 100%;
    background: #3b82f6;
    border-radius: 2px;
    transition: width 0.3s ease;
  }
}

.queue-info {
  font-size: 12px;
  color: #f59e0b;
  font-weight: 500;
}

.request-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 8px;
}

.request-actions {
  display: flex;
  justify-content: flex-end;
}

.cancel-btn {
  background: #ef4444;
  color: white;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background: #dc2626;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.queue-stats {
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  padding: 12px 20px;
}

.stats-row {
  display: flex;
  justify-content: space-around;
}

.stat-item {
  text-align: center;
  
  .stat-value {
    display: block;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
  }
  
  .stat-label {
    font-size: 11px;
    color: #6b7280;
    text-transform: uppercase;
    font-weight: 500;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes gentle-ring {
  0%, 100% {
    transform: rotate(0deg);
  }
  10%, 30% {
    transform: rotate(5deg);
  }
  20% {
    transform: rotate(-5deg);
  }
}
</style> 